import "./hotel.css";
import Navbar from "../../components/navbar/Navbar";
import Header from "../../components/header/Header";
import MailList from "../../components/mailList/MailList";
import Footer from "../../components/footer/Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleArrowLeft,
  faCircleArrowRight,
  faCircleXmark,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { useContext, useEffect, useState } from "react";
import useFetch from "../../hooks/useFetch";
import { useNavigate, useParams } from "react-router-dom";
import { SearchContext } from "../../context/SearchContext";
import useAuth from "../../hooks/useAuth";
import useBookings from "../../hooks/useBookings";
import { reviews } from "../../api/apiService";
import Reserve from "../../components/reserve/Reserve";
import LoadingSkeleton from "../../components/loading/LoadingSkeleton";
import ErrorBanner from "../../components/errorBanner/ErrorBanner";
import { getErrorMessage } from "../../api/errorUtils";
import BookingDates from "../../components/datePicker/BookingDates";
import { getBookingDateRange } from "../../utils/bookingDates";

const Hotel = () => {
  const { id } = useParams();
  const [slideNumber, setSlideNumber] = useState(0);
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const { data, loading, error, reFetch } = useFetch(`/hotels/find/${id}`, {
    mode: "object",
  });
  const {
    data: reviewsData,
    loading: reviewsLoading,
    error: reviewsError,
    reFetch: reFetchReviews,
  } = useFetch(`/hotels/${id}/reviews`, { mode: "object" });
  const { data: rooms, loading: roomsLoading } = useFetch(`/hotels/room/${id}`);

  const { user, isAuthenticated } = useAuth();
  const { bookingsList, fetchBookings } = useBookings(user?._id);
  const navigate = useNavigate();
  const { dates } = useContext(SearchContext);

  const { nights } = getBookingDateRange(dates);

  const [reviewBookingId, setReviewBookingId] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewError, setReviewError] = useState(null);
  const [reviewSuccess, setReviewSuccess] = useState(null);

  const handleOpen = (i) => {
    setSlideNumber(i);
    setOpen(true);
  };

  const handleMove = (direction) => {
    const photoCount = data?.photos?.length || 1;
    if (direction === "l") {
      setSlideNumber(slideNumber === 0 ? photoCount - 1 : slideNumber - 1);
    } else {
      setSlideNumber(slideNumber === photoCount - 1 ? 0 : slideNumber + 1);
    }
  };

  const handleBook = () => {
    if (isAuthenticated) {
      setOpenModal(true);
    } else {
      navigate("/login");
    }
  };

  useEffect(() => {
    if (user?._id) {
      fetchBookings();
    }
  }, [user?._id, fetchBookings]);

  const errorMessage = error
    ? getErrorMessage(error, "Hotel not found.")
    : !loading && !data
      ? "Hotel not found. It may have been removed or the link is invalid."
      : null;

  const reviewableBookings = bookingsList.filter((booking) => {
    if (!booking) return false;
    const hotelMatch =
      booking.hotelId === id || booking.hotelId?._id === id;
    if (!hotelMatch) return false;

    const checkout = booking.checkOutDate ? new Date(booking.checkOutDate) : null;
    const hasCheckedOut = checkout instanceof Date && !Number.isNaN(checkout) && checkout < new Date();
    if (!hasCheckedOut) return false;

    const status = (booking.status || "").toLowerCase();
    const paymentComplete = booking.paymentStatus
      ? booking.paymentStatus.toLowerCase() === "completed"
      : true;

    return ["completed", "confirmed"].includes(status) && paymentComplete;
  });

  useEffect(() => {
    if (!reviewBookingId && reviewableBookings.length > 0) {
      const firstId = reviewableBookings[0]._id || reviewableBookings[0].bookingId || "";
      if (firstId) setReviewBookingId(firstId);
    }
  }, [reviewBookingId, reviewableBookings]);

  const showContent = Boolean(!loading && data && !error);

  const handleReviewSubmit = async (event) => {
    event.preventDefault();
    setReviewError(null);
    setReviewSuccess(null);

    if (!reviewBookingId) {
      setReviewError("Please select a booking to review.");
      return;
    }
    if (!reviewComment.trim()) {
      setReviewError("Please write a comment for your review.");
      return;
    }

    try {
      await reviews.create(id, {
        rating: reviewRating,
        comment: reviewComment.trim(),
        bookingId: reviewBookingId,
      });
      setReviewSuccess("Review submitted successfully.");
      setReviewRating(5);
      setReviewComment("");
      fetchBookings();
      reFetchReviews();
    } catch (err) {
      setReviewError(getErrorMessage(err, "Unable to submit your review. Please try again."));
    }
  };

  return (
    <div>
      <Navbar />
      <Header type="list" />
      {loading ? (
        <div className="hotelContainer">
          <LoadingSkeleton count={1} type="list" />
        </div>
      ) : !showContent ? (
        <div className="hotelContainer">
          <ErrorBanner message={errorMessage || "Hotel not found."} onRetry={reFetch} />
        </div>
      ) : (
        <div className="hotelContainer">
          {open && data?.photos && (
            <div className="slider">
              <FontAwesomeIcon
                icon={faCircleXmark}
                className="close"
                onClick={() => setOpen(false)}
              />
              <FontAwesomeIcon
                icon={faCircleArrowLeft}
                className="arrow"
                onClick={() => handleMove("l")}
              />
              <div className="sliderWrapper">
                <img
                  src={data.photos[slideNumber]}
                  alt=""
                  className="sliderImg"
                />
              </div>
              <FontAwesomeIcon
                icon={faCircleArrowRight}
                className="arrow"
                onClick={() => handleMove("r")}
              />
            </div>
          )}
          <div className="hotelWrapper">
            <button type="button" className="bookNow" onClick={handleBook}>
              Reserve or Book Now!
            </button>
            <h1 className="hotelTitle">{data.name}</h1>
            <div className="hotelAddress">
              <FontAwesomeIcon icon={faLocationDot} />
              <span>{data.address}</span>
            </div>
            <span className="hotelDistance">
              Excellent location – {data.distance}m from center
            </span>
            <span className="hotelPriceHighlight">
              Book a stay over ${data.cheapestPrice} at this property
            </span>
            <div className="hotelImages">
              {data.photos?.map((photo, i) => (
                <div className="hotelImgWrapper" key={i}>
                  <img
                    onClick={() => handleOpen(i)}
                    src={photo}
                    alt=""
                    className="hotelImg"
                  />
                </div>
              ))}
            </div>
            <div className="hotelDetails">
              <div className="hotelDetailsTexts">
                <h1 className="hotelTitle">{data.title}</h1>
                <p className="hotelDesc">{data.desc}</p>
              </div>
              <div className="hotelDetailsPrice">
                <h1>Perfect for a {nights}-night stay!</h1>
                <BookingDates compact />
                <h2>
                  <b>${nights * data.cheapestPrice}</b> ({nights} nights)
                </h2>
                <button type="button" onClick={handleBook}>
                  Reserve or Book Now!
                </button>
              </div>
            </div>

            <section className="hotelReviewSection">
              <div className="reviewSummary">
                <div>
                  <h2>Guest Reviews</h2>
                  <p className="reviewSubtext">
                    Read honest feedback from verified guests and share your own experience.
                  </p>
                </div>
                <div className="reviewMetrics">
                  <span>
                    {reviewsData?.aggregates?.averageRating
                      ? `${reviewsData.aggregates.averageRating.toFixed(1)} / 5`
                      : data.rating
                        ? `${data.rating.toFixed(1)} / 5`
                        : "No rating yet"}
                  </span>
                  <span>
                    Trust score: {reviewsData?.aggregates?.trustScore ?? "—"}
                  </span>
                  <span>
                    {reviewsData?.aggregates?.totalReviews ?? 0} reviews
                  </span>
                </div>
              </div>

              {reviewsError && (
                <ErrorBanner
                  message={getErrorMessage(reviewsError, "Unable to load reviews.")}
                  onRetry={reFetchReviews}
                />
              )}

              <div className="reviewGrid">
                <div className="reviewFormCard">
                  <h3>Leave a review</h3>
                  {!isAuthenticated ? (
                    <p className="reviewHint">
                      Please log in to submit a review after your stay.
                    </p>
                  ) : reviewableBookings.length === 0 ? (
                    <p className="reviewHint">
                      Reviews are available after a completed stay. If you have already stayed here, you'll be able to submit a review once the checkout date passes.
                    </p>
                  ) : (
                    <form onSubmit={handleReviewSubmit} className="reviewForm">
                      <label>
                        Booking
                        <select
                          value={reviewBookingId}
                          onChange={(event) => setReviewBookingId(event.target.value)}
                        >
                          {reviewableBookings.map((booking) => (
                            <option key={booking._id || booking.bookingId} value={booking._id || booking.bookingId}>
                              {`Booking #${(booking._id || booking.bookingId)?.slice(-6)} · ${booking.checkInDate} → ${booking.checkOutDate}`}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Rating
                        <select
                          value={reviewRating}
                          onChange={(event) => setReviewRating(Number(event.target.value))}
                        >
                          {[5, 4, 3, 2, 1].map((value) => (
                            <option key={value} value={value}>
                              {value} star{value > 1 ? "s" : ""}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Your review
                        <textarea
                          value={reviewComment}
                          onChange={(event) => setReviewComment(event.target.value)}
                          rows={6}
                          placeholder="Share your stay experience"
                        />
                      </label>
                      {reviewError && <p className="reviewError">{reviewError}</p>}
                      {reviewSuccess && <p className="reviewSuccess">{reviewSuccess}</p>}
                      <button type="submit" className="reviewSubmitButton">
                        Submit review
                      </button>
                    </form>
                  )}
                </div>

                <div className="reviewListCard">
                  {reviewsLoading ? (
                    <p>Loading reviews...</p>
                  ) : reviewsData?.reviews?.length > 0 ? (
                    reviewsData.reviews.map((review) => (
                      <div key={review._id} className="reviewCard">
                        <div className="reviewCardHeader">
                          <div>
                            <strong>{review.userId?.username || "Guest"}</strong>
                            <span>{new Date(review.createdAt).toLocaleDateString()}</span>
                          </div>
                          <div className="reviewRatingBadge">
                            <span>{review.rating}</span>
                          </div>
                        </div>
                        <p className="reviewCommentText">
                          {review.abuseFlag ? "This review is under moderation." : review.comment}
                        </p>
                        <div className="reviewMeta">
                          {review.isVerified && <span className="verifiedBadge">Verified stay</span>}
                          {review.abuseFlag && <span className="flaggedBadge">Under review</span>}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No reviews yet. Be the first guest to share your experience.</p>
                  )}
                </div>
              </div>
            </section>

            <section className="hotelRoomsSection">
              <h2>Available rooms</h2>
              {roomsLoading ? (
                <p>Loading rooms...</p>
              ) : rooms?.length > 0 ? (
                <div className="hotelRoomsList">
                  {rooms.map((room) => (
                    <div key={room._id} className="hotelRoomCard">
                      <h3>{room.title}</h3>
                      <p>{room.desc}</p>
                      <p>
                        ${room.price}/night · Max {room.maxPeople} guests
                      </p>
                      <p className="roomNumbers">
                        Room numbers:{" "}
                        {room.roomNumbers?.map((rn) => rn.number).join(", ")}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No rooms listed for this hotel yet.</p>
              )}
            </section>
          </div>
          <MailList />
          <Footer />
        </div>
      )}
      {openModal && data && (
        <Reserve setOpen={setOpenModal} hotelId={id} hotel={data} rooms={rooms} />
      )}
    </div>
  );
};

export default Hotel;
